import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { loadScriptByURL, SITE_KEY, validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Link, useHistory } from 'react-router-dom';
import moment from 'moment';

// COMPONENTS
import Button from '../../components/Button';
import DateInput from '../../components/DateInput';
import FileUpload from '../../components/FileUpload';
import SingleSelect from '../../components/SingleSelect';

// ICONS
import { ReactComponent as ArrowLeft } from '../../assets/svg/arrow-left.svg';
import { ReactComponent as EyeOn } from '../../assets/svg/eye-on.svg';
import { ReactComponent as EyeOff } from '../../assets/svg/eye-off.svg';

// TYPES
import { UserInfoType } from './CreateAccount';
import { AccountRegistrationCommand, DomainProfileType } from '../../api/models';

interface AccountFormTypes {
	previousUrl?: string;
	returnUrl?: string;
	userInfo: UserInfoType;
	setUserInfo: React.Dispatch<React.SetStateAction<UserInfoType>>;
}

export interface FormTypes extends Omit<AccountRegistrationCommand, 'dateOfBirth'> {
	agreeToTerms?: boolean;
	overMinAge?: boolean;
	dateOfBirth?: string;
}

export const AccountForm: React.FC<AccountFormTypes> = ({ previousUrl, returnUrl, userInfo, setUserInfo }) => {
	const history = useHistory();

	// Get Website Url from Redux store
	const websiteUrl = useAppSelector((state) => state.lists.websiteUrl);

	// Error and loading state
	const [loading, setLoading] = React.useState<boolean>(false);
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const formRef = React.createRef<HTMLFormElement>();

	const [showPassword, setShowPassword] = React.useState<boolean>(false);
	const [showConfirmPassword, setShowConfirmPassword] = React.useState<boolean>(false);

	const isStudent = userInfo.profileType?.toLowerCase() === DomainProfileType.Student.toLowerCase();

	/// Setup years for student to select
	const years = () => {
		const currentYear = moment().year();

		// Student should only cover from 13-18 years old.
		const minAge = 13;
		const maxAge = 18;

		let startYear = currentYear - minAge;
		let endYear = currentYear - maxAge;

		const years = [];

		while (endYear <= startYear) {
			const year = endYear++;
			years.push({ label: year.toString(), value: year.toString() });
		}

		return years.reverse();
	};

	const goBack = () => {
		if (previousUrl) {
			history.push(previousUrl);
		} else {
			history.goBack();
		}
	};

	React.useEffect(() => {
		// load google reCAPTCHA script
		loadScriptByURL('recaptcha-key', function () {});
	}, []);

	const doCreate = async (values: FormTypes) => {
		setLoading(true);

		await window.grecaptcha.ready(() => {
			window.grecaptcha.execute(SITE_KEY, { action: 'submit' }).then((token: string) => {
				createAccount(values, token);
			});
		});
	};

	const createAccount = async (values: FormTypes, token: string) => {
		const fieldValues = {
			...values,
			organisationIdentifier: userInfo.organisationIdentifier || '',
			paymentPlanId: userInfo.paymentPlanId || undefined,
			profileType: userInfo.profileType || undefined,
			captchaToken: token || undefined
		};

		delete fieldValues.agreeToTerms;

		delete fieldValues.overMinAge;

		const response = await apibridge.doCreateAccount(fieldValues);

		setLoading(false);

		if (response.isError) {
			setFormErrorMessage(
				response.validationErrors.length ? validationErrorsToString(response.validationErrors) : response.message
			);
		} else if (response.result) {
			setUserInfo({
				email: values.email,
				parentEmail: values.parentEmail,
				organisationIdentifier: fieldValues.organisationIdentifier,
				paymentPlanId: fieldValues.paymentPlanId,
				profileType: fieldValues.profileType
			});
		}
	};

	React.useEffect(() => {
		if (formRef.current) {
			formRef.current.addEventListener('click', setFormErrorMessage.bind(null, ''));
		}

		return () => {
			if (formRef.current) {
				formRef.current.removeEventListener('click', setFormErrorMessage.bind(null, ''));
			}
		};
	}, []);

	const initialValues: FormTypes = {
		profileImageName: '',
		firstName: '',
		lastName: '',
		dateOfBirth: '',
		phoneNumber: '',
		email: '',
		parentEmail: '',
		password: '',
		confirmPassword: '',
		agreeToTerms: false,
		overMinAge: false,
		organisationIdentifier: userInfo.organisationIdentifier || '',
		paymentPlanId: userInfo.paymentPlanId || undefined,
		profileType: userInfo.profileType || undefined
	};

	// FORM VALIDATION FOR AUSTRALIAN PHONE NUMBERS
	const phoneRegExp =
		/^(?:\+?(61))? ?(?:\((?=.*\)))?(0?[2-57-8])\)? ?(\d\d(?:[- ](?=\d{3})|(?!\d\d[- ]?\d[- ]))\d\d[- ]?\d[- ]?\d{3})$/;

	const validationSchema = Yup.object({
		firstName: Yup.string().required('Required'),
		lastName: Yup.string().required('Required'),
		dateOfBirth: Yup.string().required('Required'),
		phoneNumber: Yup.string().when('profileType', {
			is: (value: DomainProfileType) =>
				value !== undefined ? value.toLowerCase() !== DomainProfileType.Student.toLowerCase() : false,
			then: Yup.string().matches(phoneRegExp, 'Phone number is not valid').required('Required')
		}),
		email: Yup.string().required('Required').email('Please enter a valid email address'),
		parentEmail: Yup.string().when('profileType', {
			is: (value: DomainProfileType) =>
				value !== undefined ? value.toLowerCase() === DomainProfileType.Student.toLowerCase() : false,
			then: Yup.string().required('Required')
		}),
		password: Yup.string()
			.required('Required')
			.min(12, 'Minimum of 12 characters')
			.matches(
				/^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{12,}$/,
				'Please enter a valid password'
			),
		confirmPassword: Yup.string()
			.required('Required')
			.oneOf([Yup.ref('password'), null], 'Passwords must match'),
		agreeToTerms: Yup.bool().required('Required').oneOf([true], 'You must agree to continue'),
		overMinAge: Yup.bool().when('profileType', {
			is: (value: DomainProfileType) =>
				value !== undefined ? value.toLowerCase() === DomainProfileType.Student.toLowerCase() : false,
			then: Yup.bool().required('Required').oneOf([true], 'You must agree to continue')
		})
	});

	return (
		<>
			<Button className="btn-text-icon go-back" onClick={goBack} buttonIcon={<ArrowLeft className="link-icon" />}>
				Back
			</Button>

			<Formik
				enableReinitialize={true}
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={doCreate}
			>
				{(formik) => {
					return (
						<Form translate="yes" ref={formRef} className="account-submit">
							<div className="header">
								<h1 className="h1">Create your account</h1>

								{!!previousUrl && (
									<p className="sub-grey-text">
										Already have an account?{' '}
										<Link
											to={{
												pathname: previousUrl,
												state: {
													organisationIdentifier: userInfo.organisationIdentifier,
													paymentPlanId: userInfo.paymentPlanId,
													profileType: userInfo.profileType
												}
											}}
											className="btn-text btn-inline"
										>
											Login here
										</Link>
									</p>
								)}
							</div>

							<div className="content">
								{!isStudent && (
									<div className="form-group">
										<label htmlFor="profileImageName">
											Upload a profile picture <span className="note">(optional)</span>
										</label>

										<FileUpload
											name="profileImageName"
											onChange={(val: string) => formik.setFieldValue('profileImageName', val)}
											multiple={false}
											maxSizeMb={5}
											imagesOnly={true}
										></FileUpload>
									</div>
								)}

								<div className="form-group">
									<label htmlFor="firstName">First name</label>

									<Field name="firstName" id="firstName" type="text" className="form-control" />

									<ErrorMessage name="firstName" component="div" className="form-validation-error" />
								</div>

								<div className="form-group">
									<label htmlFor="lastName">Last name</label>

									<Field name="lastName" id="lastName" type="text" className="form-control" />

									<ErrorMessage name="lastName" component="div" className="form-validation-error" />
								</div>

								{!isStudent ? (
									<div className="form-group">
										<label htmlFor="date">Date of birth</label>

										<DateInput
											showDropdowns={true}
											excludeFuture={true}
											minAge={true}
											dateTimeString={formik.values.dateOfBirth || ''}
											onChange={(val: string) => formik.setFieldValue('dateOfBirth', val)}
										/>
										<ErrorMessage name="dateOfBirth" component="div" className="form-validation-error" />
									</div>
								) : (
									<>
										<div className="form-group">
											<label htmlFor="date">Year of birth</label>

											<SingleSelect
												className="set-max-height"
												onChange={(val: string) => formik.setFieldValue('dateOfBirth', `01/01/${val}`)}
												options={years()}
											/>

											<ErrorMessage name="dateOfBirth" component="div" className="form-validation-error" />

											<div className="form-control-checkbox">
												<Field
													name="overMinAge"
													type="checkbox"
													onChange={() => formik.setFieldValue('overMinAge', !formik.values.overMinAge)}
													checked={formik.values.overMinAge}
												/>

												<strong>I am 13 years or older</strong>
											</div>

											<ErrorMessage name="overMinAge" component="div" className="form-validation-error" />
										</div>
									</>
								)}

								<div className="form-group">
									<label htmlFor="email">Email address</label>

									<Field name="email" id="email" type="text" className="form-control" />

									<p className="text-subtitle mtop-10">
										Please use an email address for your ChildAware account that is secure and only you have access to.
										To help keep your ChildAware account secure we will send two factor authentication codes to this
										email address.
									</p>

									<ErrorMessage name="email" component="div" className="form-validation-error" />
								</div>

								{isStudent && (
									<div className="form-group">
										<label htmlFor="parentEmail">Parent's Email address</label>

										<Field name="parentEmail" id="parentEmail" type="text" className="form-control" />

										<ErrorMessage name="parentEmail" component="div" className="form-validation-error" />
									</div>
								)}

								{!isStudent && (
									<div className="form-group">
										<label htmlFor="phoneNumber">Phone Number</label>

										<Field name="phoneNumber" id="phoneNumber" type="text" className="form-control" />
										<p className="text-subtitle">For a landline, please include an area code e.g. 03 XXX</p>

										<ErrorMessage name="phoneNumber" component="div" className="form-validation-error" />
									</div>
								)}

								<div className="form-group">
									<label htmlFor="password">Create password</label>

									<div className="password-wrapper">
										<Field
											name="password"
											id="password"
											type={showPassword ? 'text' : 'password'}
											className="form-control"
										/>

										<Button
											className="btn-text"
											buttonIcon={showPassword ? <EyeOff /> : <EyeOn />}
											onClick={() => setShowPassword(!showPassword)}
										></Button>
									</div>

									<ErrorMessage name="password" component="div" className="form-validation-error" />
								</div>

								<div className="form-group">
									<label htmlFor="password">Confirm password</label>

									<div className="password-wrapper">
										<Field
											name="confirmPassword"
											id="confirmPassword"
											type={showConfirmPassword ? 'text' : 'password'}
											className="form-control"
										/>

										<Button
											className="btn-text"
											buttonIcon={showConfirmPassword ? <EyeOff /> : <EyeOn />}
											onClick={() => setShowConfirmPassword(!showConfirmPassword)}
										></Button>
									</div>

									<ErrorMessage name="confirmPassword" component="div" className="form-validation-error" />

									<div className="note">
										<ul className="traditional-list">
											<li>Minimum of 12 characters</li>
											<li>At least one lower case letter</li>
											<li>At least one upper case letter</li>
											<li>At least one number</li>
											<li>At least one special character (e.g. ! @ ?)</li>
										</ul>
									</div>
								</div>

								<div className="form-group">
									<label className="form-control-checkbox">
										<Field type="checkbox" name="agreeToTerms" />

										{!isStudent ? (
											<span>
												I have read, understood and agree to the ChildAware Individual User{' '}
												<a href={`${websiteUrl}/terms-conditions-individual-user`} target="_blank">
													Terms and Conditions
												</a>{' '}
												and the ChildAware{' '}
												<a href={`${websiteUrl}/privacy-policy`} target="_blank">
													Privacy Policy
												</a>
												.
											</span>
										) : (
											<span>
												I have obtained permission from my parent or legal guardian to access and use the ChildAware
												Platform and they have read with me the Individual User{' '}
												<a href={`${websiteUrl}/terms-conditions-individual-user`} target="_blank">
													Terms and Conditions
												</a>{' '}
												and the{' '}
												<a href={`${websiteUrl}/privacy-policy`} target="_blank">
													Privacy Policy
												</a>{' '}
												which describe how my personal information may be handled by ChildAware.
											</span>
										)}
									</label>

									<ErrorMessage name="agreeToTerms" component="div" className="form-validation-error" />
								</div>
							</div>

							<div className="actions">
								{!!formErrorMessage && <div className="form-validation-error">{formErrorMessage}</div>}

								<Button type="submit" className="btn-primary full-width" disabled={loading}>
									Continue
								</Button>
							</div>
						</Form>
					);
				}}
			</Formik>
		</>
	);
};

export default AccountForm;
